import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { T, useTranslation } from "@redotech/redo-i18n";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { useNavigate } from "react-router-dom";
import type { MerchantCustomsGapField } from "../../setting/international/catalog-readiness-gaps";
import { OnboardingProps } from "../common";
import {
  customsReadinessPopupContent,
  CustomsReadinessPopupContent,
  loadCustomsReadinessForSession,
} from "../customs-readiness-popup";
import customsGlobeUrl from "./customs-information-assets/customs-globe.png?url";
import * as styles from "./customs-information-modal.module.css";

const heroImageProps = { alt: "", src: customsGlobeUrl };

export const CustomsReadinessModal = memo(function CustomsReadinessModal({
  handleDismiss,
  handleComplete,
}: OnboardingProps) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const rpcClient = useRequiredContext(RedoPlatformAuthRpcClientContext);
  const team = useRequiredContext(TeamContext);
  const navigate = useNavigate();

  const content = useLoad(async () => {
    const readiness = await loadCustomsReadinessForSession(rpcClient, team._id);
    return readiness ? customsReadinessPopupContent(readiness) : null;
  }, [rpcClient, team._id]);

  const handleFixNow = useHandler(() => {
    navigate(routes.getSettingsSubRoute("international/returns"));
    void handleComplete();
  });

  if (content.pending) {
    return null;
  }
  // `?onboarding-preview` can open the modal for a ready catalog; show the
  // at-risk copy with no field list then.
  const { stuckCount, gaps, gapCountsAreLowerBounds } = content.value ?? {
    stuckCount: 0,
    gaps: [],
    gapCountsAreLowerBounds: false,
  };

  return (
    <RedoBaseModal
      darkMode
      isOpen
      onModalCloseRequested={handleDismiss}
      otherClasses={[styles.modal]}
    >
      <Flex className={styles.shell} dir="row" gap="none" w="full">
        <Flex
          className={styles.main}
          dir="column"
          flex={1}
          justify="space-between"
        >
          <Flex dir="column" gap="2xl" p="5xl">
            <Text
              className={styles.eyebrow}
              fontSize="xs"
              fontWeight="medium"
              textColor="secondary"
            >
              {t("Customs information")}
            </Text>
            <Flex>
              <RedoBadge
                color="orange"
                darkMode
                size="lg"
                text={t("Action needed")}
              />
            </Flex>
            <Text
              as="h2"
              fontFamily="serif"
              fontSize="4xl"
              fontWeight="regular"
              textColor="primary"
            >
              {headline(stuckCount, t)}
            </Text>
            <Text fontSize="md" textColor="secondary">
              {stuckCount > 0
                ? t(
                    "Missing product data is delaying shipments and putting future returns at risk.",
                  )
                : t(
                    "Missing product data can delay shipments and put future returns at risk.",
                  )}
            </Text>
            {gaps.length > 0 && (
              <InformationToComplete
                atLeast={gapCountsAreLowerBounds}
                gaps={gaps}
              />
            )}
            <Flex dir="column" gap="xs">
              <Text fontSize="md" fontWeight="semibold" textColor="primary">
                {t("Fix it in as little as two minutes")}
              </Text>
              <Text fontSize="md" textColor="secondary">
                {t(
                  "Drop in your files. Ask Redo extracts the data and fills the missing fields, no formatting or manual updates needed.",
                )}
              </Text>
            </Flex>
          </Flex>
          <Flex dir="row" gap="md" p="3xl" wrap="wrap">
            <RedoButton
              hierarchy="secondary"
              onClick={() => void handleDismiss()}
              size="sm"
              text={t("Not now")}
            />
            <RedoButton
              hierarchy="primary"
              onClick={() => void handleFixNow()}
              size="sm"
              text={t("Fix it now")}
              theme="action"
            />
          </Flex>
        </Flex>
        <Flex
          {...heroImageProps}
          aria-hidden
          as="img"
          className={styles.heroImage}
          shrink={0}
        />
      </Flex>
    </RedoBaseModal>
  );
});

function headline(
  stuckCount: number,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  if (stuckCount > 0) {
    return t("{{count}} returns are blocked at customs", { count: stuckCount });
  }
  return t("Missing customs information puts future returns at risk");
}

const InformationToComplete = memo(function InformationToComplete({
  gaps,
  atLeast,
}: {
  gaps: CustomsReadinessPopupContent["gaps"];
  atLeast: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Flex dir="column" gap="xs">
      <Text fontSize="md" fontWeight="semibold" textColor="primary">
        {t("Information to complete")}
      </Text>
      {gaps.map((gap) => (
        <Text fontSize="md" key={gap.field} textColor="secondary">
          <GapLine atLeast={atLeast} count={gap.count} field={gap.field} />
        </Text>
      ))}
    </Flex>
  );
});

const gapCountComponents = {
  count: (
    <Text as="span" fontWeight="semibold" textColor="warning">
      {null}
    </Text>
  ),
};

const GapLine = memo(function GapLine({
  field,
  count,
  atLeast,
}: {
  field: MerchantCustomsGapField;
  count: number;
  /** Render the count as a floor, e.g. "100,000+". */
  atLeast: boolean;
}) {
  const formatted = count.toLocaleString();
  const values = { productCount: atLeast ? `${formatted}+` : formatted };
  switch (field) {
    case "originCountry":
      return (
        <T
          components={gapCountComponents}
          i18nKey="Country of origin — <count>{{productCount}}</count> products"
          values={values}
        />
      );
    case "manufacturerId":
      return (
        <T
          components={gapCountComponents}
          i18nKey="Manufacturer details — <count>{{productCount}}</count> products"
          values={values}
        />
      );
    case "hsCode":
      return (
        <T
          components={gapCountComponents}
          i18nKey="HS code — <count>{{productCount}}</count> products"
          values={values}
        />
      );
    case "customsDescription":
      return (
        <T
          components={gapCountComponents}
          i18nKey="Customs description — <count>{{productCount}}</count> products"
          values={values}
        />
      );
    case "merchantProductId":
      return (
        <T
          components={gapCountComponents}
          i18nKey="SKU — <count>{{productCount}}</count> products"
          values={values}
        />
      );
    case "manufacturerProductId":
      return (
        <T
          components={gapCountComponents}
          i18nKey="Manufacturer part number — <count>{{productCount}}</count> products"
          values={values}
        />
      );
    case "standardizedProductId":
      return (
        <T
          components={gapCountComponents}
          i18nKey="Barcode — <count>{{productCount}}</count> products"
          values={values}
        />
      );
  }
});
